import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import { IconButton, TextButton, Text } from '@oneaudi/unified-web-components';
import { ACTIONS, SELECTORS } from '@oneaudi/stck-store';
import { useI18n } from '@oneaudi/i18n-context';
import {
  VehicleBasic,
  getRelatedProduct,
  useServicesContext,
  ConsumptionTileElementPLP,
  EnvKConsumptionAndEmissions,
} from '@oneaudi/vtp-shared';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { getAppContext } from '../../hooks/tileHook';
import {
  trackAddToFav,
  trackDetailsPageNav,
  trackLayerClose,
  trackLayerLinkClick,
  trackLayerOpen,
  trackRemoveFromFav,
} from '../../tracking/tilesTracking';
import {
  ButtonContainer,
  ConsumptionEmissionContainer,
  FavoritesContainer,
  FavoritesOverlay,
  GridButtonContainer,
  LinkButtonContainer,
} from './ActionButtons.styles';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { FeatureAppLoader } from '@feature-hub/react';
import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import { GfaLayerManagerV1 } from '@volkswagen-onehub/gfa-layer-manager';
import { useVehicleDetailsPageUrl } from '../../hooks/useVehicleDetailsPageUrl';

interface Props {
  vehicle: VehicleBasic;
}

const ActionButtons: React.FC<Props> = ({ vehicle }) => {
  const detailsPageLink = useVehicleDetailsPageUrl(vehicle) || '';
  const goToDetailsLabel = useI18n({
    id: 'nemo.ui.sc.result.details.button',
    defaultMessage: 'Go to Details',
  });
  const addFavoriteLabel = useI18n({ id: 'nemo.ui.sc.result.addfavorite', defaultMessage: '' });
  const favoriteAreaLabelNotSaved = useI18n({
    id: 'stockcars.button.favourites.is-not-saved.arialabel',
    defaultMessage: '',
  });
  const favoriteAreaLabelSaved = useI18n({
    id: 'stockcars.button.favourites.is-saved.arialabel',
    defaultMessage: '',
  });
  const removeFavoriteLabel = useI18n({
    id: 'nemo.ui.sc.result.removefavorite',
    defaultMessage: '',
  });
  const favoritesLayerHeadline = useI18n({
    id: 'nemo.ui.sc.favorites.layer.headline',
    defaultMessage: '',
  });
  const favoritesLayerCopy = useI18n({ id: 'nemo.ui.sc.favorites.layer.copy', defaultMessage: '' });
  const favoritesLayerYes = useI18n({
    id: 'nemo.ui.sc.favorites.layer.button.yes',
    defaultMessage: 'Yes',
  });
  const favoritesLayerNo = useI18n({
    id: 'nemo.ui.sc.favorites.layer.button.no',
    defaultMessage: 'No',
  });
  const consumptionLayerLabel = useI18n({ id: 'nemo.ui.sc.tiles.consumption.link.text' });

  const appContext = getAppContext();
  const dispatch = useDispatch();
  const isFavorite = useSelector((state) =>
    SELECTORS.VEHICLES.isFavoriteVehicle(state, vehicle.id)
  );
  const [showFavoritesLayer, setShowFavoritesLayer] = useState(false);

  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;
  const localeService = useServicesContext().getAdditionalService(
    'gfa:locale-service'
  ) as LocaleServiceV1;
  const layerManager = useServicesContext().getAdditionalService(
    'gfa:layer-manager'
  ) as GfaLayerManagerV1;
  const isDEMarket = localeService.countryCode?.toLowerCase() === 'de';
  const relatedProduct = getRelatedProduct(vehicle);
  const getConfigurationObj = useServicesContext().getConfiguration();

  const scStartPageUrl = getConfigurationObj?.urls?.scStartPageLink;
  const consumptionVersion = getConfigurationObj?.featureApps?.consumptionVersion || '4.3.0';

  const handleFavoritesClick = () => {
    if (isFavorite) {
      if (appContext !== 'results') {
        setShowFavoritesLayer(true);
      } else {
        removeFavorites();
      }
    } else {
      dispatch(
        ACTIONS.FAVORITE_VEHICLES.addFavoriteVehicleId({
          vehicleId: vehicle.id,
        })
      );
      trackAddToFav(trackingService, addFavoriteLabel, relatedProduct);
    }
  };
  const removeFavorites = () => {
    dispatch(
      ACTIONS.FAVORITE_VEHICLES.removeFavoriteVehicleId({
        vehicleId: vehicle.id,
      })
    );
    setShowFavoritesLayer(false);
    trackRemoveFromFav(trackingService, removeFavoriteLabel, relatedProduct);
  };
  const openLayer = () => {
    layerManager?.openFocusLayer(
      () => (
        <div className="tileLayer">
          <div data-fefa-custom-id="tileConsumption">
            <FeatureAppLoader
              featureAppId="tileConsumption"
              src={`https://fa-vtp-consumption.cdn.prod.arcade.apps.one.audi/${consumptionVersion}/fh/app.js`}
              config={{
                vehicleId: vehicle.id,
                eecImageUrl: `https://fa-vtp-consumption.cdn.prod.arcade.apps.one.audi/${consumptionVersion}/static`,
              }}
            />
          </div>
          <FeatureAppLoader
            featureAppId="tile-footnotes"
            src="https://fa-footnote-engine.cdn.prod.arcade.apps.one.audi/v2.4.8/fh/app.js"
            config={{
              apiUrls: [
                `${scStartPageUrl}/commons/sc_footnotes/_jcr_content/content/sc_footnotes.json`,
              ],
              omitSideSpacing: true,
              omitBottomSpacing: true,
              uiTheme: 'light',
              layerElementClassName: 'tileLayer',
            }}
          />
        </div>
      ),
      {},
      {
        size: FocusLayerSizeV2.A,
        userCloseable: true,
        onClose: () => {
          if (trackingService) {
            trackLayerClose(trackingService, 'vtp emission layer');
          }
        },
      }
    );
    trackLayerLinkClick(trackingService, consumptionLayerLabel, 'text link', relatedProduct);
    trackLayerOpen(trackingService, 'emission layer', 'vtp emission layer', undefined);
  };
  const hasConsumptionValues = (veh: VehicleBasic | undefined) => {
    return (
      veh?.io?.hasWltp ||
      veh?.io?.hasNedc ||
      veh?.vlsEnergyProvision?.hasWltp ||
      veh?.vlsEnergyProvision?.hasNedc
    );
  };

  return (
    <>
      <ButtonContainer>
        <TextButton
          variant="primary"
          data-testid="detailsBtn"
          href={detailsPageLink}
          isFullWidth
          onClick={() =>
            trackDetailsPageNav(
              trackingService,
              `${vehicle.symbolicCarline?.description}`,
              detailsPageLink,
              'button'
            )
          }
        >
          {goToDetailsLabel}
        </TextButton>
        <FavoritesContainer>
          <IconButton
            variant="secondary"
            icon={isFavorite ? 'favorite-active' : 'favorite'}
            size="md"
            type="button"
            onClick={handleFavoritesClick}
            data-testid="favoritesBtn"
            aria-label={isFavorite ? favoriteAreaLabelSaved : favoriteAreaLabelNotSaved}
          />
        </FavoritesContainer>
      </ButtonContainer>
      <>
        {isDEMarket && (
          <div>
            <EnvKConsumptionAndEmissions vehicle={vehicle} data-testid="pkv-envkv" isPlp />
          </div>
        )}
        {!isDEMarket && hasConsumptionValues(vehicle) && (
          <ConsumptionEmissionContainer>
            <ConsumptionTileElementPLP
              vehicleId={vehicle.id}
              order={['consumption', 'emission']}
              displayedItems={['emission', 'consumption']}
              hideLayerBtn
              isPlp
              data-testid="consumptionElement"
            />
            <LinkButtonContainer>
              <TextButton
                variant="tertiary"
                size="sm"
                icon="forward"
                iconPosition="trailing"
                onClick={openLayer}
                data-testid="consumptionLayer"
              >
                {consumptionLayerLabel}
              </TextButton>
            </LinkButtonContainer>
          </ConsumptionEmissionContainer>
        )}
      </>
      {showFavoritesLayer && (
        <ThemeProvider theme={{ name: 'dark' }}>
          <FavoritesOverlay>
            <Text variant={{ order: '3', style: 'bold', type: 'copy' }}>
              {favoritesLayerHeadline}
            </Text>
            <Text variant={{ order: '2', style: 'normal', type: 'copy' }}>
              {favoritesLayerCopy}
            </Text>
            <GridButtonContainer data-testid="removeFavoriteLayer">
              <TextButton variant="primary" onClick={removeFavorites} data-testid="removeFavorite">
                {favoritesLayerYes}
              </TextButton>
              <TextButton
                variant="secondary"
                onClick={() => setShowFavoritesLayer(false)}
                data-testid="closeRemoveFavoriteLayer"
              >
                {favoritesLayerNo}
              </TextButton>
            </GridButtonContainer>
          </FavoritesOverlay>
        </ThemeProvider>
      )}
    </>
  );
};

export default ActionButtons;
